/**
 * English messages: the source of the key set. Every key of docs/PORTAL-PLAN.md
 * section 5.4. `{x}` are interpolations (see translate.ts). Add a key here AND
 * in es.ts: es is typed against this file, so a missing key fails the typecheck,
 * and i18n.test.ts checks parity and empty values.
 */
export const en = {
  "app.title": "Spoonity account",
  "app.skipToContent": "Skip to content",
  "lang.toggle": "Español",
  "lang.label": "Language",
  "login.heading": "Sign in to your account",
  "login.intro": "Enter the email address where you receive Spoonity invoices.",
  "login.emailLabel": "Email address",
  "login.submit": "Send code",
  "login.invalidEmail": "Enter a valid email address.",
  "login.haveCode": "I already have a code",
  "login.cooldown": "A new code cannot be sent for {seconds} s. If you already received one, use 'I already have a code' below.",
  "code.heading": "Check your email",
  "code.intro": "If {email} is registered for a Spoonity account, we sent a 6-digit code. It expires in 10 minutes.",
  "code.introNoEmail": "Enter the 6-digit code from the email we sent you. It expires 10 minutes after it was sent.",
  "code.label": "6-digit code",
  "code.submit": "Sign in",
  "code.resend": "Send a new code",
  "code.resendIn": "You can request a new code in {seconds} s",
  "code.resent": "A new code is on its way.",
  "code.changeEmail": "Use a different email",
  "code.needNewCode": "I need a new code",
  "code.invalid": "That code is not correct. {n} attempts left.",
  "code.expired": "This code has expired. Request a new one.",
  "code.tooMany": "Too many attempts. Request a new code or try again later.",
  "code.format": "Enter the 6 digits from the email.",
  "code.noEmail": "Did not get it? Check spam, or ask your Spoonity contact to confirm your billing email.",
  "picker.heading": "Choose an account",
  "picker.intro": "Your email has access to these accounts.",
  "picker.balance": "Balance {amount}",
  "picker.overdue": "{amount} overdue",
  "nav.overview": "Overview",
  "nav.invoices": "Invoices",
  "nav.payments": "Payments",
  "nav.statement": "Statement",
  "nav.switchAccount": "Switch account",
  "nav.signOut": "Sign out",
  "nav.signOutAll": "Sign out on all devices",
  "nav.signedInAs": "Signed in as {email}",
  "nav.sections": "Account sections",
  "overview.balance": "Amount owing",
  "overview.overdue": "Overdue",
  "overview.credits": "Credits available",
  "overview.nextDue": "Next payment due",
  "overview.nextDueValue": "Invoice {number}, {amount}, due {date}",
  "overview.nothingDue": "Nothing due. Thank you!",
  "overview.aging": "Open balance by age",
  "overview.recentInvoices": "Recent invoices",
  "overview.recentPayments": "Recent payments",
  "overview.viewAll": "View all",
  "aging.current": "Not yet due",
  "aging.1-30": "1 to 30 days",
  "aging.31-60": "31 to 60 days",
  "aging.61-90": "61 to 90 days",
  "aging.90+": "Over 90 days",
  "common.dataAsOf": "Updated {time}",
  "common.loading": "Loading",
  "common.retry": "Try again",
  "common.back": "Back",
  "common.download": "Download PDF",
  "common.print": "Print",
  "invoices.heading": "Invoices",
  "invoices.tab.open": "Open",
  "invoices.tab.paid": "Paid",
  "invoices.tab.all": "All",
  "invoices.search": "Search by invoice number",
  "invoices.from": "From",
  "invoices.to": "To",
  "invoices.col.number": "Invoice",
  "invoices.col.date": "Date",
  "invoices.col.due": "Due",
  "invoices.col.total": "Total",
  "invoices.col.balance": "Balance",
  "invoices.col.status": "Status",
  "invoices.loadMore": "Show more",
  "status.open": "Open",
  "status.overdue": "Overdue {days} days",
  "status.paid": "Paid",
  "status.partial": "Partially paid",
  "invoice.heading": "Invoice {number}",
  "invoice.date": "Invoice date",
  "invoice.due": "Due date",
  "invoice.terms": "Terms",
  "invoice.billTo": "Bill to",
  "invoice.memo": "Note",
  "invoice.lines": "Line items",
  "invoice.col.description": "Description",
  "invoice.col.qty": "Qty",
  "invoice.col.rate": "Rate",
  "invoice.col.amount": "Amount",
  "invoice.subtotal": "Subtotal",
  "invoice.tax": "Tax",
  "invoice.total": "Total",
  "invoice.paid": "Paid",
  "invoice.balance": "Balance due",
  "invoice.payments": "Payments applied",
  "invoice.payNow": "Pay now",
  "invoice.payNowNote": "Opens Spoonity's secure payment page by Stripe.",
  "invoice.stripeManaged": "This invoice is paid through Stripe. Use the payment link in Stripe's email or the card on file.",
  "invoice.contactToPay": "To pay this invoice, contact your Spoonity billing contact.",
  "invoice.pdfPending": "The PDF is not available yet. Try again later.",
  "payments.heading": "Payments",
  "payments.col.date": "Date",
  "payments.col.amount": "Amount",
  "payments.col.reference": "Reference",
  "payments.col.applied": "Applied to",
  "payments.unapplied": "Unapplied {amount}",
  "statement.heading": "Statement of account",
  "statement.period": "Period",
  "statement.preset.thisMonth": "This month",
  "statement.preset.lastMonth": "Last month",
  "statement.preset.last3": "Last 3 months",
  "statement.preset.ytd": "Year to date",
  "statement.preset.custom": "Custom",
  "statement.opening": "Opening balance",
  "statement.closing": "Closing balance",
  "statement.charges": "Invoices",
  "statement.credits": "Payments and credits",
  "statement.col.date": "Date",
  "statement.col.type": "Type",
  "statement.col.reference": "Reference",
  "statement.col.charge": "Charge",
  "statement.col.credit": "Credit",
  "statement.col.balance": "Balance",
  "statement.kind.invoice": "Invoice",
  "statement.kind.payment": "Payment",
  "statement.kind.credit": "Credit note",
  "statement.unreliable": "Opening and closing balances are not available for this account online. Contact us for a full statement.",
  "statement.historyLimit": "Statements are available from {date}.",
  "statement.invalidPeriod": "Choose a start date on or before the end date.",
  "statement.apply": "Apply",
  "empty.invoicesOpen": "No open invoices.",
  "empty.invoicesPaid": "No paid invoices in this period.",
  "empty.invoices": "No invoices in this period.",
  "empty.payments": "No payments in this period.",
  "empty.statement": "No activity in this period.",
  "signedOut.heading": "You are signed out",
  "signedOut.again": "Sign in again",
  "expired.heading": "Your session has ended",
  "expired.body": "For your security, we sign you out after 30 minutes without activity.",
  "unavailable.heading": "The account portal is temporarily unavailable",
  "unavailable.body": "Please try again later.",
  "error.rateLimited": "Too many requests. Try again in {seconds} s.",
  "error.generic": "Something went wrong. Try again.",
  "error.network": "You seem to be offline. Check your connection.",
  "error.notFound": "We could not find that page.",
  "footer.help": "Questions about your account? Contact your Spoonity billing contact.",
  "footer.privacy": "Privacy",
  "testAccount.title": "TEST ACCOUNT",
  "testAccount.body": "Sample data for verifying the portal. This is not a real customer account and nothing here is owed.",
} as const;

export type MessageKey = keyof typeof en;
